import type { BillData } from '@shared/schema/billData';
import { findBySellerPan, type BillRecord } from '@/lib/db/billsRepo';
import { levenshteinDistance } from '@/utils/levenshtein';

export type DuplicateSeverity = 'exact' | 'possible';

export interface DuplicateMatch {
  severity: DuplicateSeverity;
  bill: BillRecord;
}

function normalizedBillNumber(value: string | null): string | null {
  return value ? value.trim().toLowerCase() : null;
}

/**
 * Never blocks by itself — Review UI surfaces the result and requires the
 * user to explicitly confirm before proceeding, per spec ("Do not silently
 * block the user unless the IRD website itself confirms duplication").
 * Queries only bills sharing the same seller PAN (via the IndexedDB index),
 * not a full history scan.
 */
export async function findDuplicates(billData: BillData, excludeBillId?: string): Promise<DuplicateMatch[]> {
  const sellerPan = billData.sellerPan.value;
  if (!sellerPan) return [];

  const candidates = (await findBySellerPan(sellerPan)).filter((c) => c.id !== excludeBillId);
  const billNumber = normalizedBillNumber(billData.billNumber.value);
  const amount = billData.amount.value;
  const billDateAd = billData.billDateAd.value;

  const matches: DuplicateMatch[] = [];
  for (const candidate of candidates) {
    const candidateBillNumber = normalizedBillNumber(candidate.billData.billNumber.value);
    const candidateAmount = candidate.billData.amount.value;
    const candidateDate = candidate.billData.billDateAd.value;

    if (billNumber !== null && candidateBillNumber !== null && billNumber === candidateBillNumber) {
      matches.push({ severity: 'exact', bill: candidate });
      continue;
    }

    if (amount === null || candidateAmount === null || amount !== candidateAmount) continue;

    const sameDate = billDateAd !== null && candidateDate !== null && billDateAd === candidateDate;
    const nearMatchBillNumber =
      billNumber !== null && candidateBillNumber !== null && levenshteinDistance(billNumber, candidateBillNumber) <= 1;

    if (sameDate || nearMatchBillNumber) {
      matches.push({ severity: 'possible', bill: candidate });
    }
  }
  return matches;
}
